import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useEffect } from 'react'
import { useUser, UserProfile } from '@clerk/clerk-react'
import { useSubscription } from '@clerk/clerk-react/experimental'
import { useConvexFeatures } from '../hooks/useConvexFeatures'
import { useQuery } from "convex/react"
import { api } from "../convex/_generated/api"

/**
 * Account Management Page
 *
 * Features:
 * - User profile display with Clerk integration
 * - Real subscription status from Clerk Billing API (experimental)
 * - Real credit balance from Convex database
 * - Plan upgrade/downgrade options
 * - Usage statistics and recent jobs
 * - Account settings
 *
 * Note: Uses Clerk Billing for subscription management (not Stripe subscriptions)
 * Stripe is only used for dynamic credit purchases via checkout
 */

const AccountPage: React.FC = () => {
  const { isSignedIn, isLoaded, user } = useUser()
  const navigate = useNavigate()

  // Convex data fetching
  const {
    userJobs,
    isJobsLoading,
    isConvexEnabled
  } = useConvexFeatures()

  // Fetch real credit balance from Convex
  // No clerkId needed - automatically uses authenticated user from ctx.auth
  const creditBalance = useQuery(
    api.credits.getBalance,
    user?.id ? {} : "skip"
  )

  // Fetch real subscription data from Clerk Billing (experimental API)
  const { data: subscription } = useSubscription({ for: 'user' })

  // Map Clerk subscription data to plan tiers
  // Note: subscriptionItems[0] contains the plan info
  const firstSubscriptionItem = subscription?.subscriptionItems?.[0]
  const planName = firstSubscriptionItem?.id || 'free'

  const currentPlan: 'free' | 'apprentice' | 'professional' | 'enterprise' =
    planName.toLowerCase().includes('apprentice') ? 'apprentice' :
    planName.toLowerCase().includes('professional') ? 'professional' :
    planName.toLowerCase().includes('enterprise') ? 'enterprise' :
    'free'

  // Auto-select annual billing when billing plans page loads
  useEffect(() => {
    const checkAndSelectAnnual = () => {
      // Wait for Clerk component to load
      const intervalToggles = document.querySelectorAll('[role="switch"], button[aria-label*="annual"], button[aria-label*="year"]')

      intervalToggles.forEach((toggle) => {
        const toggleElement = toggle as HTMLElement
        const isAnnualToggle =
          toggleElement.textContent?.toLowerCase().includes('annual') ||
          toggleElement.textContent?.toLowerCase().includes('year') ||
          toggleElement.getAttribute('aria-label')?.toLowerCase().includes('annual') ||
          toggleElement.getAttribute('aria-label')?.toLowerCase().includes('year')

        // Check if toggle is not already selected
        const isSelected =
          toggleElement.getAttribute('aria-checked') === 'true' ||
          toggleElement.classList.contains('selected') ||
          toggleElement.classList.contains('active')

        if (isAnnualToggle && !isSelected) {
          toggleElement.click()
        }
      })
    }

    // Check multiple times as Clerk loads asynchronously
    const timeouts = [500, 1000, 1500, 2000].map(delay =>
      setTimeout(checkAndSelectAnnual, delay)
    )

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout))
    }
  }, [])

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading account...</p>
        </div>
      </div>
    )
  }

  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Sign In Required</h1>
            <p className="text-gray-600 mb-6">Please sign in to access your account.</p>
            <button
              onClick={() => navigate({ to: '/', search: { jobId: undefined } })}
              className="bg-purple-600 text-white px-6 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'free': return 'Free'
      case 'apprentice': return 'Apprentice'
      case 'professional': return 'Professional'
      case 'enterprise': return 'Enterprise'
      default: return 'Unknown'
    }
  }

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'free': return 'bg-gray-100 text-gray-800'
      case 'apprentice': return 'bg-purple-100 text-purple-800'
      case 'professional': return 'bg-blue-100 text-blue-800'
      case 'enterprise': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <header className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/sorting_cat_mascot.png" alt="SortingCat Mascot" className="h-16 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Settings</h1>
          <p className="text-lg text-purple-600 font-medium">Manage your subscription and preferences</p>
        </header>

        {/* Compact Plan & Credits Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1">
              {/* Current Plan */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Plan:</span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPlanColor(currentPlan)}`}>
                  {getPlanDisplayName(currentPlan)}
                </span>
              </div>

              {/* Current Credits */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Credits:</span>
                <span className="text-lg font-bold text-gray-900">
                  {creditBalance !== undefined ? creditBalance.balance.toLocaleString() : '...'}
                </span>
              </div>
            </div>

            {/* Upgrade Plan Button */}
            <a
              href="/account#/billing/plans?interval=year"
              className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 transition-colors text-center whitespace-nowrap"
            >
              Upgrade Plan
            </a>
          </div>
        </div>

        {/* Clerk UserProfile Component */}
        <style>{`
          /* Hide Clerk's header and footer */
          .cl-userProfile-root .cl-header {
            display: none !important;
          }
          .cl-userProfile-root .cl-footer {
            display: none !important;
          }

          /* Remove all shadows and borders from Clerk components */
          .cl-userProfile-root,
          .cl-userProfile-root .cl-card,
          .cl-userProfile-root > div,
          .cl-userProfile-root > div > div {
            box-shadow: none !important;
            border: none !important;
          }

          /* Ensure card is transparent and full width */
          .cl-userProfile-root .cl-card {
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
          }

          /* Ensure all Clerk containers can expand to full width */
          .cl-pageScrollBox,
          .cl-page,
          .cl-profilePage,
          .cl-cardBox,
          .cl-scrollBox,
          .cl-rootBox,
          .cl-card,
          .cl-main {
            max-width: 100% !important;
            width: 100% !important;
            overflow: visible !important;
          }

          /* Remove extra bottom margin from last section */
          .cl-userProfile-root .cl-profileSection:last-child {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
        `}</style>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="p-4 sm:p-6 overflow-x-auto">
            <UserProfile
              appearance={{
                elements: {
                  rootBox: {
                    width: '100%',
                    margin: 0,
                    padding: 0
                  },
                  card: {
                    boxShadow: 'none',
                    border: 'none',
                    borderRadius: '0',
                    padding: '0',
                    margin: '0',
                    width: '100%',
                    backgroundColor: 'transparent'
                  },
                  navbar: {
                    display: 'none'
                  },
                  navbarMobileMenuButton: {
                    display: 'none'
                  },
                  pageScrollBox: {
                    padding: '0',
                    margin: 0,
                    width: '100%'
                  },
                  scrollBox: {
                    position: 'static',
                    padding: '0',
                    margin: 0,
                    width: '100%'
                  },
                  page: {
                    gap: '1rem',
                    width: '100%',
                    padding: '0',
                    margin: 0
                  },
                  profilePage: {
                    gap: '1rem',
                    width: '100%',
                    padding: '0',
                    margin: 0
                  },
                  profileSection: {
                    marginBottom: '1.5rem',
                    gap: '0.75rem',
                    width: '100%',
                    padding: '0',
                    margin: '0 0 1.5rem 0'
                  },
                  profileSectionContent: {
                    gap: '0.75rem',
                    width: '100%',
                    padding: 0
                  },
                  profileSectionItem: {
                    width: '100%',
                    paddingTop: '0.75rem',
                    paddingBottom: '0.75rem',
                    '@media (min-width: 640px)': {
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem'
                    }
                  },
                  profileSectionPrimaryButton: {
                    fontSize: '0.875rem',
                    paddingTop: '0.5rem',
                    paddingBottom: '0.5rem',
                    paddingLeft: '1rem',
                    paddingRight: '1rem',
                    width: '100%',
                    flexShrink: 0,
                    '@media (min-width: 640px)': {
                      width: 'auto',
                      minWidth: '120px'
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Delete Account Section */}
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4 mb-8">
          <a
            href="/account#/security"
            className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition-colors inline-block text-center whitespace-nowrap"
          >
            Delete Account
          </a>
        </div>

        {/* Real Job Statistics from Convex */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {isConvexEnabled ? 'Real-time Usage Statistics' : 'Usage This Month'}
          </h2>
          
          {isConvexEnabled && isJobsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
              <p className="text-gray-600">Loading job statistics...</p>
            </div>
          ) : isConvexEnabled && userJobs ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {userJobs.reduce((sum, job) => sum + (job.fileCount || 0), 0)}
                  </div>
                  <p className="text-gray-600">Images Processed</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {userJobs.filter(job => job.status === 'completed').length}
                  </div>
                  <p className="text-gray-600">Jobs Completed</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {userJobs.filter(job => job.status === 'pending' || job.status === 'processing').length}
                  </div>
                  <p className="text-gray-600">Jobs In Progress</p>
                </div>
              </div>
              
              {/* Recent Jobs */}
              {userJobs.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Jobs</h3>
                  <div className="space-y-3">
                    {userJobs.slice(0, 3).map((job) => (
                      <div key={job._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {job.fileCount} files • {job.categoryMode} mode
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(job.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          job.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {job.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            // Fallback mock data when Convex is disabled
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">1,247</div>
                  <p className="text-gray-600">Images Processed</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">23</div>
                  <p className="text-gray-600">Jobs Completed</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {creditBalance !== undefined ? creditBalance.balance.toLocaleString() : '...'}
                  </div>
                  <p className="text-gray-600">Credits Remaining</p>
                  {creditBalance && (
                    <p className="text-gray-500 text-sm mt-2">
                      {creditBalance.totalConsumed.toLocaleString()} used this period
                    </p>
                  )}
                </div>
              </div>
              
              {currentPlan !== 'free' && (
                <div className="mt-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Credit Usage</span>
                    <span>1,247 / 10,000</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: '12.47%' }}
                    ></div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Support Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex justify-center">
            <p className="text-gray-600 text-center max-w-md">
              Need help with anything or have a special use case for SortingCat? Email{' '}
              <a href="mailto:support@sortingcat.ai" className="text-purple-600 hover:text-purple-700 font-medium">
                support@sortingcat.ai
              </a>
              {' '}and we&apos;ll sort it out!
            </p>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <button
            onClick={() => navigate({ to: '/', search: { jobId: undefined } })}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors inline-flex items-center cursor-pointer"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back
          </button>
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/account')({
  component: AccountPage,
})